/**
 * Agenda: selecionar paciente → confirmar → ir direto ao atendimento.
 * Reusa ProntoAtendimentoPatientStep; sem passo de pagamento.
 */
import { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import {
  ProntoAtendimentoPatientStep,
  type ProntoSelectedPatient,
} from "@/components/schedule/prontoAtendimento/ProntoAtendimentoPatientStep";
import { usePatientEvolution } from "./usePatientEvolution";

interface EvolutionFromScheduleDialogProps {
  open: boolean;
  onClose: () => void;
  schedules?: fhir5.Schedule[];
  onSuccess?: () => void;
}

type Step = "patient" | "confirm";

export function EvolutionFromScheduleDialog({
  open,
  onClose,
  schedules,
  onSuccess,
}: EvolutionFromScheduleDialogProps) {
  const [step, setStep] = useState<Step>("patient");
  const [patient, setPatient] = useState<ProntoSelectedPatient | null>(null);
  const { loading, startOrContinueEvolution, professionalDisplay } =
    usePatientEvolution({ schedules });

  useEffect(() => {
    if (!open) return;
    setStep("patient");
    setPatient(null);
  }, [open]);

  const handlePatientContinue = (selected: ProntoSelectedPatient) => {
    setPatient(selected);
    setStep("confirm");
  };

  const handleConfirm = async () => {
    if (!patient) return;
    const ok = await startOrContinueEvolution({
      reference: patient.reference,
      display: patient.display,
      cpf: patient.cpf,
      responsibleCpf: patient.responsibleCpf,
    });
    if (ok) {
      onSuccess?.();
      onClose();
    }
  };

  const handleClose = () => {
    if (loading) return;
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {step === "patient" ? "Evolução — selecionar paciente" : "Iniciar evolução"}
      </DialogTitle>
      <DialogContent dividers>
        {step === "patient" ? (
          <ProntoAtendimentoPatientStep onContinue={handlePatientContinue} />
        ) : (
          <>
            <Typography variant="body1" fontWeight={600}>
              Paciente: {patient?.display}
            </Typography>
            {professionalDisplay && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Profissional: {professionalDisplay}
              </Typography>
            )}
           
          </>
        )}
      </DialogContent>
      {step === "confirm" && (
        <DialogActions>
          <Button
            onClick={() => setStep("patient")}
            disabled={loading}
            sx={{ textTransform: "none" }}
          >
            Voltar
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => void handleConfirm()}
            disabled={loading}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {loading ? "Iniciando..." : "Iniciar evolução"}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}
